import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Clock, CheckCircle, Shield, ArrowRight, Star, Globe, Users, Eye } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './ApplyOnline.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ApplyOnline = () => {
  const navigate = useNavigate();
  const [visaProducts, setVisaProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  // Handle card click - increment view and navigate
  const handleCardClick = async (product) => {
    // Increment view count in background
    try {
      axios.patch(`${BACKEND_URL}/api/visa-products/${product.id}/increment-views`);
    } catch (error) {
      console.log('View count update skipped');
    }
    // Navigate to detail page
    navigate(`/apply/${product.slug}`);
  };

  useEffect(() => {
    fetchVisaProducts();
    
    // Close dropdown on outside click
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchVisaProducts = async () => {
    try {
      // Fetch all active visa products, not just featured
      const res = await axios.get(`${BACKEND_URL}/api/visa-products`);
      setVisaProducts(res.data);
    } catch (error) {
      console.error('Error fetching visa products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    try {
      const res = await axios.get(`${BACKEND_URL}/api/visa-products/search?q=${encodeURIComponent(query)}`);
      setSearchResults(res.data);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error searching:', error);
    }
  };

  const handleSelectCountry = (slug) => {
    setShowDropdown(false);
    setSearchQuery('');
    navigate(`/apply/${slug}`);
  };

  const stats = [
    { icon: Users, value: '1,500+', label: 'Visas Processed' },
    { icon: Star, value: '96%', label: 'Success Rate' },
    { icon: Globe, value: '50+', label: 'Countries' },
  ];

  return (
    <div className="apply-online-page">
      <Header />
      
      {/* Hero Section - Warm tones instead of blue/purple */}
      <section className="apply-hero">
        <div className="apply-hero-bg">
          <div className="apply-hero-overlay"></div>
        </div>
        <div className="apply-hero-content">
          <div className="apply-hero-badge">
            <Shield size={16} />
            <span>Trusted by 1,500+ Customers</span>
          </div>
          <h1>Your Visa Journey<br /><span>Starts Here</span></h1>
          <p className="apply-hero-subtitle">
            Simple, transparent, and hassle-free visa applications.
            Let our experts handle the paperwork while you plan your adventure.
          </p>
          
          {/* Search Bar */}
          <div className="apply-search-container" ref={searchRef}>
            <div className="apply-search-box">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Where would you like to go?"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && setShowDropdown(true)}
              />
            </div>
            
            {/* Search Dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div className="apply-search-dropdown">
                {searchResults.map((product) => (
                  <div
                    key={product.id}
                    className="search-result-item"
                    onClick={() => handleSelectCountry(product.slug)}
                  >
                    <img src={product.flag_url} alt={product.country_name} className="search-flag" />
                    <div className="search-result-info">
                      <span className="search-country-name">{product.country_name}</span>
                      <span className="search-visa-type">{product.visa_type}</span>
                    </div>
                    <div className="search-processing-time">
                      <Clock size={14} />
                      <span>{product.processing_time}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="apply-hero-stats">
            {stats.map((stat, index) => (
              <div key={index} className="hero-stat-item">
                <stat.icon size={20} />
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Destinations */}
      <section className="apply-destinations">
        <div className="section-container">
          <div className="section-header">
            <h2>Popular Destinations</h2>
            <p>Choose your destination and start your visa application</p>
          </div>
          
          {loading ? (
            <div className="loading-grid">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="destination-card-skeleton"></div>
              ))}
            </div>
          ) : (
            <div className="destinations-grid">
              {visaProducts.map((product) => (
                <div
                  key={product.id}
                  className="destination-card"
                  onClick={() => handleCardClick(product)}
                >
                  <div className="destination-image">
                    <img src={product.thumbnail_image} alt={product.country_name} />
                    <div className="destination-overlay">
                      <div className="visas-badge views-badge">
                        <Eye size={12} />
                        <span>{product.visas_processed?.toLocaleString()}+</span>
                      </div>
                    </div>
                  </div>
                  <div className="destination-info">
                    <div className="destination-header">
                      <img src={product.flag_url} alt="" className="country-flag" />
                      <h3>{product.country_name}</h3>
                    </div>
                    <p className="visa-type">{product.visa_type}</p>
                    <div className="destination-meta">
                      <div className="meta-item">
                        <Clock size={14} />
                        <span>{product.processing_time}*</span>
                      </div>
                      <div className="meta-item success-rate">
                        <Star size={14} />
                        <span>{product.success_rate}% Success</span>
                      </div>
                    </div>
                    <button className="apply-btn">
                      Apply Now <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="apply-how-it-works">
        <div className="section-container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Simple 4-step process to get your visa</p>
          </div>
          
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon"><MapPin size={24} /></div>
              <h3>Select Destination</h3>
              <p>Choose your destination country and visa type</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon"><Users size={24} /></div>
              <h3>Fill Details</h3>
              <p>Provide your travel and personal information</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon"><CheckCircle size={24} /></div>
              <h3>Review & Submit</h3>
              <p>Review your application and submit</p>
            </div>
            <div className="step-card">
              <div className="step-number">4</div>
              <div className="step-icon"><Shield size={24} /></div>
              <h3>Expert Assistance</h3>
              <p>Our consultants handle the rest</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="apply-trust">
        <div className="section-container">
          <div className="trust-content">
            <h2>Why Choose Visaasure?</h2>
            <div className="trust-grid">
              <div className="trust-item">
                <CheckCircle size={20} />
                <div>
                  <h4>Expert Guidance</h4>
                  <p>Dedicated visa consultants for your application</p>
                </div>
              </div>
              <div className="trust-item">
                <CheckCircle size={20} />
                <div>
                  <h4>Transparent Pricing</h4>
                  <p>No hidden fees, clear breakdown of all costs</p>
                </div>
              </div>
              <div className="trust-item">
                <CheckCircle size={20} />
                <div>
                  <h4>Document Assistance</h4>
                  <p>Help with document preparation and verification</p>
                </div>
              </div>
              <div className="trust-item">
                <CheckCircle size={20} />
                <div>
                  <h4>Real-time Updates</h4>
                  <p>Track your application status anytime</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Embassy Disclaimer */}
      <section className="apply-disclaimer">
        <div className="section-container">
          <p>
            <strong>Disclaimer:</strong> Visa approval is solely at the discretion of the respective embassy/consulate. 
            Visaasure assists with application preparation only. We do not guarantee visa approval.
          </p>
          <p className="eta-disclaimer">
            <strong>*ETA:</strong> Processing times shown are averages based on past successful cases and may vary on a case-to-case basis.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ApplyOnline;
